import { Box, CircularProgress, Color } from '@material-ui/core';
import { Alert, Skeleton } from '@material-ui/lab';
import { LenderApprovalStatus } from '@nemuru-eng/js-api-client';
import { useEffect } from 'react';

import { ParallelStrategy } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/components/ParallelStrategy';
import { ProcessingStrategySummary } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/components/ProcessingStrategySummary';
import { WaterfallStrategy } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/components/WaterfallStrategy';
import { ProcessingStatusContextProvider } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/context/ProcessingStrategyContext';
import { AbstractProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/AbstractProcessingStrategyEntity';
import { ParallelProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/ParallelProcessingStrategyEntity';
import { WaterfallProcessingStrategyEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/entities/ProcessingStrategy/WaterfallProcessingStrategyEntity';
import { useProcessingStrategy } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/hooks/useProcessingStrategy';
import { ProcessingStatus } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/components/ProcessingStrategy/types/ProcessingStatus';
import { AbstractLoanPaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/_shared/entities/PaymentMethodDetail/AbstractLoanPaymentMethodDetailEntity';
import { WaitForDocumentationRequiredTimeoutException } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/Loan/Loan/_shared/exceptions/WaitForDocumentationRequiredTimeoutException';
import { FlexColumn } from '@/components/shared/components/FlexColumn';
import { FlexRow } from '@/components/shared/components/FlexRow';
import { Font } from '@/components/shared/components/Font';
import { Icon } from '@/components/shared/components/Icon';
import { Text } from '@/components/shared/components/Text';
import { useTranslation } from '@/components/shared/hooks/useTranslation';
import { useCustomTheme } from '@/hooks/useCustomTheme';

interface ProcessingStrategyProps {
  paymentMethodDetailEntity: AbstractLoanPaymentMethodDetailEntity;
  onProcessingCompleted?: (processingStrategy?: AbstractProcessingStrategyEntity) => void;
  onChoosingCompleted?: (processingStrategy: AbstractProcessingStrategyEntity) => void;
  onError?: () => void;
  options?: {
    showStatusMessage?: boolean;
  };
}

export const ProcessingStrategy = (props: ProcessingStrategyProps) => {
  return (
    <ProcessingStatusContextProvider>
      <ProcessingStrategyModule {...props} />
    </ProcessingStatusContextProvider>
  );
};

// Show status message when the whole processing strategy is
// being executed (e.g. after submitting Merchant POS form)
export const ProcessingStrategyModule = ({
  paymentMethodDetailEntity,
  onProcessingCompleted,
  onChoosingCompleted,
  onError,
  options = {
    showStatusMessage: false,
  },
}: ProcessingStrategyProps) => {
  const {
    processingStatus,
    processingStrategyEntity,
    waitUntilLoanApplicationsAreProcessed,
    waitUntilDocumentationRequiredFilesExist,
    setProcessingStatus,
  } = useProcessingStrategy();

  const { customBorderRadius, palette } = useCustomTheme();

  useEffect(() => {
    process();
  }, []);

  useEffect(() => {
    if (
      onChoosingCompleted &&
      processingStrategyEntity &&
      processingStrategyEntity.hasSelectedLoanApplication() &&
      processingStrategyEntity.isBorrowerSelection()
    ) {
      onChoosingCompleted(processingStrategyEntity);
    }
  }, [processingStrategyEntity]);

  const process = async () => {
    let processingStrategy: AbstractProcessingStrategyEntity | undefined;
    try {
      processingStrategy = await waitUntilLoanApplicationsAreProcessed(paymentMethodDetailEntity);
      setProcessingStatus('syncingDocs');
      await waitUntilDocumentationRequiredFilesExist(processingStrategy, paymentMethodDetailEntity);
      setProcessingStatus('syncedDocs');
      if (onProcessingCompleted && processingStrategy) {
        onProcessingCompleted(processingStrategy);
      }
    } catch (error) {
      console.log('error', error);
      setProcessingStatus(null);
      if (error instanceof WaitForDocumentationRequiredTimeoutException) {
        if (onProcessingCompleted) {
          onProcessingCompleted(processingStrategy);
        }
        return;
      }
      if (onError) onError();
    }
  };

  if (!processingStrategyEntity || !processingStrategyEntity.paymentMethodConditions) {
    return (
      <div style={{ minHeight: '400px' }}>
        <FlexColumn gridGap={16}>
          <Skeleton variant="rect" width="100%" height={116} style={{ borderRadius: customBorderRadius.paper }} />
          <Skeleton variant="rect" width="100%" height={116} style={{ borderRadius: customBorderRadius.paper }} />
          <Skeleton variant="rect" width="100%" height={116} style={{ borderRadius: customBorderRadius.paper }} />
        </FlexColumn>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '400px' }}>
      <ProcessingStrategySummary processingStrategyEntity={processingStrategyEntity} />
      {processingStrategyEntity instanceof ParallelProcessingStrategyEntity && (
        <ParallelStrategy processingStatus={processingStatus} processingStrategyEntity={processingStrategyEntity} />
      )}
      {processingStrategyEntity instanceof WaterfallProcessingStrategyEntity && (
        <WaterfallStrategy processingStatus={processingStatus} processingStrategyEntity={processingStrategyEntity} />
      )}
      {options.showStatusMessage && processingStatus && (
        <StatusMessage processingStatus={processingStatus} processingStrategyEntity={processingStrategyEntity} />
      )}
      {processingStrategyEntity.hasSelectedLoanApplication() && (
        <Box mt={4}>
          <FlexRow color={palette.primary.main} justifyContent="flex-end">
            <Icon size={20}>horizontal_rule</Icon>
            <Font variant="caption" display="block">
              <Text tId="SELECTED_LOAN_APPLICATION" />
            </Font>
          </FlexRow>
        </Box>
      )}
    </div>
  );
};

interface StatusMessageProps {
  processingStatus: ProcessingStatus;
  processingStrategyEntity: AbstractProcessingStrategyEntity;
}

const StatusMessage = ({ processingStatus, processingStrategyEntity }: StatusMessageProps) => {
  const { getTextIdTranslation } = useTranslation();
  const { palette, customBorderRadius } = useCustomTheme();

  const selectedLoanApplication = processingStrategyEntity.getSelectedLoanApplication();

  const lenderName = selectedLoanApplication?.lenderName ?? null;
  const lenderApprovalStatus = selectedLoanApplication?.lenderFields.loanApplicationStatus.lenderApprovalStatus ?? null;

  const hasSelectedLoanApplication = processingStrategyEntity.hasSelectedLoanApplication();
  const isManualChoose = processingStrategyEntity.isBorrowerSelection();
  const atLeastOneProcessedWithLenderRequestId =
    processingStrategyEntity.loanApplications?.some(
      (l) => l.isProcessedOrSelected() && l.lenderRequestId !== undefined,
    ) ?? false;

  const allowChoose = isManualChoose && atLeastOneProcessedWithLenderRequestId && hasSelectedLoanApplication === false;

  const color = {
    [LenderApprovalStatus.YES]: 'success',
    [LenderApprovalStatus.NO]: 'error',
    [LenderApprovalStatus.MAYBE]: 'warning',
  };

  const icon = {
    [LenderApprovalStatus.YES]: 'check',
    [LenderApprovalStatus.NO]: 'close',
    [LenderApprovalStatus.MAYBE]: 'hourglass_empty',
  };

  const statusColor = isManualChoose || lenderApprovalStatus === null ? 'info' : color[lenderApprovalStatus];
  const statusIcon = allowChoose
    ? 'ads_click'
    : lenderApprovalStatus === null
    ? 'hourglass_empty'
    : icon[lenderApprovalStatus];

  return (
    <Box width="100%" mt={4}>
      <Alert
        // @ts-expect-error - statusColor is a valid color
        severity={statusColor as unknown as Color}
        style={{ borderRadius: customBorderRadius.paper, padding: 16 }}
        icon={
          <Box display="flex" alignItems="center" justifyContent="center" width={22} height={22} flexShrink={0}>
            {processingStatus === 'syncedDocs' && statusIcon ? (
              <Icon size={18} style={{ color: palette[statusColor].main }}>
                {statusIcon}
              </Icon>
            ) : (
              <CircularProgress size={18} style={{ color: palette.info.main }} />
            )}
          </Box>
        }
      >
        <Box display="flex" flexDirection={'column'} gridGap={16}>
          <Box>
            <Font variant="body2" display="block">
              <Text
                strong
                tId={
                  processingStatus === 'syncedDocs' && allowChoose
                    ? 'SELECT_LOAN_APPLICATION_MANUAL_CHOOSE'
                    : processingStatus === 'syncedDocs'
                    ? `SELECT_LOAN_APPLICATION_${lenderApprovalStatus ?? 'no_status'}`
                    : processingStatus === 'syncingDocs'
                    ? 'DOCUMENTATION_SYNC_IN_PROGRESS'
                    : 'PROCESSING_STRATEGY_IN_PROGRESS'
                }
              />
            </Font>
            <Font variant="body2" display="block">
              <Text
                tId={
                  processingStatus === 'syncedDocs' && allowChoose
                    ? 'SELECT_LOAN_APPLICATION_MANUAL_CHOOSE_TEXT'
                    : processingStatus === 'syncedDocs'
                    ? `SELECT_LOAN_APPLICATION_${lenderApprovalStatus ?? 'no_status'}_TEXT`
                    : processingStatus === 'syncingDocs'
                    ? 'DOCUMENTATION_SYNC_IN_PROGRESS_TEXT'
                    : 'PROCESSING_STRATEGY_IN_PROGRESS_TEXT'
                }
                dynamicDataTexts={[getTextIdTranslation(lenderName ?? '')]}
              />
            </Font>
          </Box>
        </Box>
      </Alert>
    </Box>
  );
};
